import logger from "../utils/logger";

/**
 * Get user's current location using browser geolocation API
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000, // 5 minutes cache
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        logger.log("📍 Location detected:", coords);
        resolve(coords);
      },
      (error) => {
        logger.error("❌ Location error:", error);
        reject(error);
      },
      options
    );
  });
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lon1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lon2 - Second longitude
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @returns {number}
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Filter restaurants by distance from user's location
 * @param {Array} restaurants - Array of restaurant objects
 * @param {Object} userLocation - User's coordinates {latitude, longitude}
 * @param {number} maxDistance - Maximum distance in kilometers (default: 50km)
 * @returns {Array} Filtered and sorted restaurants with distance
 */
export const filterRestaurantsByDistance = (
  restaurants,
  userLocation,
  maxDistance = 50
) => {
  if (!userLocation || !restaurants) return restaurants;

  return restaurants
    .map((restaurant) => {
      // Only calculate distance if restaurant has coordinates
      if (restaurant.location?.latitude && restaurant.location?.longitude) {
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          restaurant.location.latitude,
          restaurant.location.longitude
        );
        return { ...restaurant, distance };
      }
      return { ...restaurant, distance: null };
    })
    .filter((restaurant) => {
      // Include restaurants without coordinates or within distance
      return restaurant.distance === null || restaurant.distance <= maxDistance;
    })
    .sort((a, b) => {
      // Sort by distance, null distances go to end
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
};

/**
 * Format distance for display
 * @param {number} distance - Distance in kilometers
 * @returns {string} Formatted distance string
 */
export const formatDistance = (distance) => {
  if (distance === null || distance === undefined) return "";
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m away`;
  }
  return `${distance}km away`;
};
